import { calculateAll } from '../../../calculations/taxCalculations';

const round2 = (num) => Math.round(num * 100) / 100;

/**
 * Computes the impact of a percentage salary raise using the existing tax engine
 * 
 * @param {Object} input - RaiseImpactInput
 * @param {number} input.currentAnnualGross - Current annual gross salary
 * @param {number} input.raisePct - Raise percentage (0-100)
 * @param {Object} [input.context] - Engine context (region, student loans, pension, etc.)
 * @returns {Object} RaiseImpactResult
 */
export const computeRaiseImpact = (input) => {
  const { currentAnnualGross, raisePct, context = {} } = input;

  // Validation
  if (currentAnnualGross < 0) {
    throw new Error('Current salary must be non-negative');
  }

  if (raisePct < 0) {
    throw new Error('Raise percentage must be non-negative');
  }

  if (raisePct > 100) {
    throw new Error('Raise percentage must not exceed 100');
  }

  // Calculate gross amounts
  const grossBefore = currentAnnualGross;
  const grossAfter = currentAnnualGross * (1 + raisePct / 100);
  const deltaGrossAnnual = grossAfter - grossBefore;

  // Build user input objects for the tax engine
  const baseUserInput = {
    salary: grossBefore,
    year: '2025',
    overtime: { include: false },
    ...context
  };

  const raisedUserInput = {
    salary: grossAfter,
    year: '2025',
    overtime: { include: false },
    ...context
  };

  // Use the existing tax engine to calculate both scenarios
  const beforeResult = calculateAll(baseUserInput, context.config);
  const afterResult = calculateAll(raisedUserInput, context.config);

  // Extract net annual amounts (takeHome is already annual)
  const netBeforeAnnual = beforeResult.takeHome;
  const netAfterAnnual = afterResult.takeHome;
  const deltaNetAnnual = netAfterAnnual - netBeforeAnnual;
  const deltaNetMonthly = deltaNetAnnual / 12;

  // Calculate derived metrics
  const effectiveRaisePctNet = netBeforeAnnual > 0 ? deltaNetAnnual / netBeforeAnnual : 0;
  const keepPerPound = deltaGrossAnnual > 0 ? deltaNetAnnual / deltaGrossAnnual : 0;

  return {
    grossBefore: round2(grossBefore),
    grossAfter: round2(grossAfter),
    netBeforeAnnual: round2(netBeforeAnnual),
    netAfterAnnual: round2(netAfterAnnual),
    deltaGrossAnnual: round2(deltaGrossAnnual),
    deltaNetAnnual: round2(deltaNetAnnual),
    deltaNetMonthly: round2(deltaNetMonthly),
    effectiveRaisePctNet: round2(effectiveRaisePctNet),
    keepPerPound: round2(keepPerPound)
  };
};